import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, LifeBuoy } from 'lucide-react';
import helpCenterService, { HelpArticleSummary, HelpOverview } from '../../services/helpCenterService';
import { groupByAudience, sortArticles } from '../../utils/helpArticleNav';
import ArrowScrollRow from '../common/ArrowScrollRow';
import { t } from '../../i18n';

// ============================================
// HelpArticlesTeaser (2026-10-22, Marcel: „unter der Lösungen-Sektion eine
// weitere Sektion ALLER Hilfecenter-Artikel in einer horizontalen Liste –
// einfach nur für SEO“). Startseiten-Block „Hilfe & Anleitungen“: JEDER
// veröffentlichte Hilfe-Artikel als Karte (Zielgruppe · Kategorie, Titel,
// Beschreibung) in einem Scroller mit Pfeil-Buttons – alle Links stehen im
// DOM, damit Google jeden Artikel von der Startseite aus findet. Ohne
// Artikel rendert der Block nichts (keine leere Überschrift).
// ============================================

export const HELP_CARD_WIDTH = 260;
export const HELP_CARD_GAP = 12;
export const HELP_SCROLL_STEP = HELP_CARD_WIDTH + HELP_CARD_GAP;

export interface HelpTeaserItem {
  article: HelpArticleSummary;
  /** Kurzname der Heimat-Zielgruppe (z. B. „Spieler“), leer bei altem Backend ohne Zielgruppen */
  audience: string;
  /** Kategoriename, leer wenn der Artikel keiner Kategorie zugeordnet ist */
  category: string;
}

/**
 * ALLE Artikel genau einmal, in Lesereihenfolge des Hilfecenters: Zielgruppen nach sortOrder,
 * darin Kategorien nach sortOrder, darin Artikel nach sortOrder/Titel. Querverweise zählen nicht
 * doppelt (nur die Heimat-Kategorie). Ohne Zielgruppen (altes Backend): Kategorien nach sortOrder,
 * danach Artikel ohne Kategorie. PURE.
 */
export const flattenHelpArticles = (overview: HelpOverview | null | undefined): HelpTeaserItem[] => {
  if (!overview) return [];
  const items: HelpTeaserItem[] = [];
  const seen = new Set<string>();
  const push = (article: HelpArticleSummary, audience: string, category: string) => {
    if (seen.has(article.id)) return;
    seen.add(article.id);
    items.push({ article, audience, category });
  };
  const groups = groupByAudience(overview.audiences ?? [], overview.categories ?? [], overview.articles ?? [], overview.defaultAudience || 'veranstalter');
  for (const group of groups) {
    for (const section of group.sections) section.articles.forEach((a) => push(a, group.audience.short, section.category?.name ?? ''));
  }
  // Fallback/Rest: Kategorien ohne Zielgruppe (altes Backend) und Artikel ohne Kategorie
  const cats = [...(overview.categories ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const sorted = sortArticles(overview.articles ?? []);
  for (const cat of cats) sorted.filter((a) => a.categoryId === cat.id).forEach((a) => push(a, '', cat.name));
  sorted.forEach((a) => push(a, '', ''));
  return items;
};

const HelpArticlesTeaser: React.FC<{ className?: string }> = ({ className }) => {
  const [overview, setOverview] = useState<HelpOverview | null>(null);

  useEffect(() => {
    let alive = true;
    helpCenterService.getOverview().then((o) => { if (alive) setOverview(o); }).catch(() => { if (alive) setOverview(null); });
    return () => { alive = false; };
  }, []);

  const items = flattenHelpArticles(overview);
  if (items.length === 0) return null;

  return (
    <section aria-label={t('web.component.help-articles-teaser.hilfe-anleitungen')} data-testid="help-articles-teaser" className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.help-articles-teaser.hilfe-anleitungen')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('web.component.help-articles-teaser.untertitel', { count: items.length })}</p>
        </div>
        <Link to="/hilfe" className="mr-1 flex shrink-0 items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1]" data-testid="help-articles-teaser-all">
          {t('web.component.help-articles-teaser.zum-hilfecenter')} <ArrowRight size={13} />
        </Link>
      </div>

      <ArrowScrollRow overlay as="ul" testId="help-articles-teaser" wrapperTestId="help-articles-teaser-rail" step={HELP_SCROLL_STEP} compact scrollClassName="flex snap-x snap-mandatory gap-3 pb-1">
        {items.map(({ article, audience, category }) => {
          const label = [audience, category].filter(Boolean).join(' · ');
          return (
            <li key={article.id} className="shrink-0 snap-start" style={{ width: HELP_CARD_WIDTH }}>
              <Link
                to={`/hilfe/${article.slug}`}
                data-testid={`help-teaser-${article.slug}`}
                className="group flex h-full min-h-[150px] flex-col rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 transition hover:border-[#3fced1]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
              >
                <span className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#3fced1]">
                  <LifeBuoy size={12} aria-hidden="true" />
                  <span className="truncate">{label || t('web.component.help-articles-teaser.hilfe-anleitungen')}</span>
                </span>
                <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-white">{article.title}</h3>
                {article.description && <p className="mt-1.5 line-clamp-3 text-[13px] leading-relaxed text-gray-400">{article.description}</p>}
              </Link>
            </li>
          );
        })}
      </ArrowScrollRow>
    </section>
  );
};

export default HelpArticlesTeaser;
